import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { DocumentViewer } from './DocumentViewer';
import { 
  Search,
  Filter,
  Download,
  Eye,
  FileText,
  Shield,
  Lock,
  Building,
  Clock,
  Star,
  StarOff,
  Share2,
  AlertCircle,
  CheckCircle,
  XCircle,
  Calendar,
  User,
  ExternalLink,
  BookOpen,
  Briefcase,
  Users,
  Settings,
  Heart,
  DollarSign,
  Zap,
  RefreshCw,
  SortAsc,
  SortDesc,
  Grid,
  List,
  Bookmark,
  TrendingUp,
  Archive,
  Bell
} from 'lucide-react';

interface PolicyDocument {
  id: string;
  title: string;
  description: string;
  category: string;
  department: string;
  classification: 'Public' | 'Internal' | 'Confidential' | 'Restricted';
  accessLevel: 'All Employees' | 'Managers Only' | 'HR Only' | 'Executive Team';
  lastUpdated: string;
  version: string;
  fileSize: string;
  downloadCount: number;
  hasAccess: boolean;
  isFavorite: boolean;
  status: 'Current' | 'Under Review' | 'Archived' | 'Draft';
  tags: string[];
  author: string;
  approvalDate: string;
  nextReview: string;
}

export function Policies() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [selectedClassification, setSelectedClassification] = useState('all');
  const [sortBy, setSortBy] = useState('updated');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<string | null>(null);
  const [isDocumentViewerOpen, setIsDocumentViewerOpen] = useState(false);
  const [favoriteDocuments, setFavoriteDocuments] = useState<string[]>([]);
  const [requestedAccess, setRequestedAccess] = useState<string[]>([]);

  // Mock policy documents data
  const [documents] = useState<PolicyDocument[]>([
    {
      id: '1',
      title: 'Employee Handbook - Section 4.2: Time Off and Leave Policies',
      description: 'Comprehensive guide to PTO, sick leave, holidays, and family leave policies including eligibility, accrual rates, and approval processes.',
      category: 'HR Policies',
      department: 'Human Resources',
      classification: 'Internal',
      accessLevel: 'All Employees',
      lastUpdated: '2024-01-15',
      version: '3.1',
      fileSize: '2.1 MB',
      downloadCount: 342,
      hasAccess: true,
      isFavorite: false,
      status: 'Current',
      tags: ['PTO', 'Leave', 'Benefits', 'Time Off'],
      author: 'Jennifer Martinez',
      approvalDate: '2024-01-15',
      nextReview: '2025-01-15'
    },
    {
      id: '2',
      title: 'Information Security Policy',
      description: 'Guidelines for protecting company data, password requirements, access controls, and incident reporting procedures.',
      category: 'Security',
      department: 'IT Security',
      classification: 'Confidential',
      accessLevel: 'All Employees',
      lastUpdated: '2024-01-10',
      version: '4.2',
      fileSize: '1.8 MB',
      downloadCount: 256,
      hasAccess: true,
      isFavorite: true,
      status: 'Current',
      tags: ['Security', 'Data Protection', 'Passwords', 'Compliance'],
      author: 'Michael Chang',
      approvalDate: '2024-01-10',
      nextReview: '2024-07-10'
    },
    {
      id: '3',
      title: 'Code of Conduct and Ethics',
      description: 'Standards of behavior, ethical guidelines, conflict of interest policies, and reporting procedures for all employees.',
      category: 'Ethics & Compliance',
      department: 'Legal',
      classification: 'Internal',
      accessLevel: 'All Employees',
      lastUpdated: '2023-12-01',
      version: '2.8',
      fileSize: '1.4 MB',
      downloadCount: 489,
      hasAccess: true,
      isFavorite: false,
      status: 'Current',
      tags: ['Ethics', 'Conduct', 'Compliance', 'Standards'],
      author: 'Sarah Williams',
      approvalDate: '2023-12-01',
      nextReview: '2024-12-01'
    },
    {
      id: '4',
      title: 'Remote Work Policy and Guidelines',
      description: 'Eligibility requirements, equipment provision, security protocols, and performance expectations for remote employees.',
      category: 'Workplace Policies',
      department: 'Human Resources',
      classification: 'Internal',
      accessLevel: 'All Employees',
      lastUpdated: '2024-01-08',
      version: '1.5',
      fileSize: '980 KB',
      downloadCount: 178,
      hasAccess: true,
      isFavorite: true,
      status: 'Current',
      tags: ['Remote Work', 'WFH', 'Equipment', 'Security'],
      author: 'David Park',
      approvalDate: '2024-01-08',
      nextReview: '2024-07-08'
    },
    {
      id: '5',
      title: 'Benefits Administration Guide 2024',
      description: 'Complete overview of health insurance, retirement plans, wellness programs, and enrollment procedures.',
      category: 'Benefits',
      department: 'Human Resources',
      classification: 'Confidential',
      accessLevel: 'All Employees',
      lastUpdated: '2023-11-15',
      version: '4.1',
      fileSize: '3.2 MB',
      downloadCount: 412,
      hasAccess: true,
      isFavorite: false,
      status: 'Current',
      tags: ['Benefits', 'Insurance', '401k', 'Wellness'],
      author: 'Benefits Team',
      approvalDate: '2023-11-15',
      nextReview: '2024-10-01'
    },
    {
      id: '6',
      title: 'Executive Compensation Framework',
      description: 'Detailed compensation structure, bonus calculations, and equity participation for executive-level positions.',
      category: 'Compensation',
      department: 'Executive',
      classification: 'Restricted',
      accessLevel: 'Executive Team',
      lastUpdated: '2024-01-01',
      version: '2.0',
      fileSize: '1.1 MB',
      downloadCount: 23,
      hasAccess: false,
      isFavorite: false,
      status: 'Current',
      tags: ['Compensation', 'Executive', 'Bonus', 'Equity'],
      author: 'Board of Directors',
      approvalDate: '2024-01-01',
      nextReview: '2024-06-01'
    },
    {
      id: '7',
      title: 'Workplace Safety and Emergency Procedures',
      description: 'Safety protocols, emergency evacuation plans, incident reporting, and first aid procedures for all locations.',
      category: 'Safety & Compliance',
      department: 'Facilities',
      classification: 'Internal',
      accessLevel: 'All Employees',
      lastUpdated: '2023-10-20',
      version: '3.4',
      fileSize: '2.7 MB',
      downloadCount: 267,
      hasAccess: true,
      isFavorite: false,
      status: 'Under Review',
      tags: ['Safety', 'Emergency', 'Procedures', 'First Aid'],
      author: 'Facilities Team',
      approvalDate: '2023-10-20',
      nextReview: '2024-04-20'
    },
    {
      id: '8',
      title: 'Anti-Harassment and Discrimination Policy',
      description: 'Zero-tolerance policy for harassment and discrimination, reporting procedures, and investigation processes.',
      category: 'HR Policies',
      department: 'Human Resources',
      classification: 'Internal',
      accessLevel: 'All Employees',
      lastUpdated: '2023-09-15',
      version: '2.1',
      fileSize: '1.6 MB',
      downloadCount: 298,
      hasAccess: true,
      isFavorite: false,
      status: 'Current',
      tags: ['Harassment', 'Discrimination', 'Reporting', 'Investigation'],
      author: 'Legal & HR',
      approvalDate: '2023-09-15',
      nextReview: '2024-09-15'
    },
    {
      id: '9',
      title: 'Data Privacy and GDPR Compliance Manual',
      description: 'Data handling procedures, privacy rights, GDPR compliance requirements, and breach response protocols.',
      category: 'Security',
      department: 'Legal',
      classification: 'Confidential',
      accessLevel: 'Managers Only',
      lastUpdated: '2023-11-30',
      version: '1.8',
      fileSize: '2.4 MB',
      downloadCount: 89,
      hasAccess: false,
      isFavorite: false,
      status: 'Current',
      tags: ['Privacy', 'GDPR', 'Data Protection', 'Compliance'],
      author: 'Legal Team',
      approvalDate: '2023-11-30',
      nextReview: '2024-05-30'
    },
    {
      id: '10',
      title: 'Travel and Expense Policy',
      description: 'Guidelines for business travel, expense reporting, approval processes, and reimbursement procedures.',
      category: 'Finance',
      department: 'Finance',
      classification: 'Internal',
      accessLevel: 'All Employees',
      lastUpdated: '2023-08-10',
      version: '2.5',
      fileSize: '1.3 MB',
      downloadCount: 156,
      hasAccess: true,
      isFavorite: true,
      status: 'Current',
      tags: ['Travel', 'Expenses', 'Reimbursement', 'Finance'],
      author: 'Finance Team',
      approvalDate: '2023-08-10',
      nextReview: '2024-08-10'
    }
  ]);

  // Filter and sort documents
  const filteredAndSortedDocuments = documents
    .filter(doc => {
      const matchesSearch = doc.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          doc.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          doc.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
      const matchesCategory = selectedCategory === 'all' || doc.category === selectedCategory;
      const matchesDepartment = selectedDepartment === 'all' || doc.department === selectedDepartment;
      const matchesClassification = selectedClassification === 'all' || doc.classification === selectedClassification;
      
      return matchesSearch && matchesCategory && matchesDepartment && matchesClassification;
    })
    .sort((a, b) => {
      let comparison = 0;
      switch (sortBy) {
        case 'title':
          comparison = a.title.localeCompare(b.title);
          break;
        case 'updated':
          comparison = new Date(a.lastUpdated).getTime() - new Date(b.lastUpdated).getTime();
          break;
        case 'downloads':
          comparison = a.downloadCount - b.downloadCount;
          break;
        case 'category':
          comparison = a.category.localeCompare(b.category);
          break;
        default:
          comparison = 0;
      }
      return sortOrder === 'asc' ? comparison : -comparison;
    });

  // Get unique categories, departments, and classifications for filters
  const categories = [...new Set(documents.map(doc => doc.category))];
  const departments = [...new Set(documents.map(doc => doc.department))];
  const classifications = [...new Set(documents.map(doc => doc.classification))];

  // Get recent and popular documents
  const recentDocuments = documents
    .filter(doc => doc.hasAccess)
    .sort((a, b) => new Date(b.lastUpdated).getTime() - new Date(a.lastUpdated).getTime())
    .slice(0, 3);

  const popularDocuments = documents
    .filter(doc => doc.hasAccess)
    .sort((a, b) => b.downloadCount - a.downloadCount)
    .slice(0, 3);

  const favoritesList = documents.filter(doc => favoriteDocuments.includes(doc.id));

  const handleToggleFavorite = (docId: string) => {
    setFavoriteDocuments(prev => 
      prev.includes(docId) 
        ? prev.filter(id => id !== docId)
        : [...prev, docId]
    );
  };

  const handleRequestAccess = (docId: string) => {
    setRequestedAccess(prev => [...prev, docId]);
    // In a real app, this would make an API call to request access
  };

  const handleViewDocument = (docTitle: string) => {
    setSelectedDocument(docTitle);
    setIsDocumentViewerOpen(true);
  };

  const getClassificationColor = (classification: string) => {
    switch (classification) {
      case 'Public': return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      case 'Internal': return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'Confidential': return 'bg-amber-50 text-amber-700 border-amber-200';
      case 'Restricted': return 'bg-red-50 text-red-700 border-red-200';
      default: return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  const getClassificationIcon = (classification: string) => {
    switch (classification) {
      case 'Public': return Eye;
      case 'Internal': return Building;
      case 'Confidential': return Shield;
      case 'Restricted': return Lock;
      default: return FileText;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Current': return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      case 'Under Review': return 'bg-yellow-50 text-yellow-700 border-yellow-200';
      case 'Draft': return 'bg-gray-50 text-gray-700 border-gray-200';
      case 'Archived': return 'bg-red-50 text-red-700 border-red-200';
      default: return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  const DocumentCard = ({ document }: { document: PolicyDocument }) => {
    const ClassificationIcon = getClassificationIcon(document.classification);
    const isFav = favoriteDocuments.includes(document.id);
    const isRequested = requestedAccess.includes(document.id);

    return (
      <Card className="group hover:shadow-lg transition-all duration-200 border-gray-200 dark:border-gray-700">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <CardTitle className="text-base leading-tight mb-2 group-hover:text-blue-600 transition-colors">
                {document.title}
              </CardTitle>
              <p className="text-sm text-muted-foreground line-clamp-2 leading-relaxed">
                {document.description}
              </p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleToggleFavorite(document.id)}
              className="p-1 h-auto opacity-0 group-hover:opacity-100 transition-opacity"
            >
              {isFav ? (
                <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
              ) : (
                <StarOff className="h-4 w-4 text-gray-400 hover:text-yellow-400" />
              )}
            </Button>
          </div>
        </CardHeader>

        <CardContent>
          <div className="space-y-4">
            {/* Classification and Status */}
            <div className="flex flex-wrap items-center gap-2">
              <Badge className={`${getClassificationColor(document.classification)} text-xs font-medium`}>
                <ClassificationIcon className="h-3 w-3 mr-1" />
                {document.classification}
              </Badge>
              <Badge className={`${getStatusColor(document.status)} text-xs font-medium`}>
                {document.status}
              </Badge>
              <Badge variant="outline" className="text-xs">
                {document.category}
              </Badge>
            </div>

            {/* Document Metadata */}
            <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
              <div className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {new Date(document.lastUpdated).toLocaleDateString()}
              </div>
              <div className="flex items-center gap-1">
                <FileText className="h-3 w-3" />
                v{document.version}
              </div>
              <div className="flex items-center gap-1">
                <Download className="h-3 w-3" />
                {document.downloadCount} downloads
              </div>
              <div className="flex items-center gap-1">
                <User className="h-3 w-3" />
                {document.fileSize}
              </div>
            </div>

            {/* Tags */}
            <div className="flex flex-wrap gap-1">
              {document.tags.slice(0, 3).map((tag, index) => (
                <Badge key={index} variant="secondary" className="text-xs px-2 py-0">
                  {tag}
                </Badge>
              ))}
              {document.tags.length > 3 && (
                <Badge variant="secondary" className="text-xs px-2 py-0">
                  +{document.tags.length - 3}
                </Badge>
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2 pt-2 border-t">
              {document.hasAccess ? (
                <>
                  <Button
                    size="sm"
                    onClick={() => handleViewDocument(document.title)}
                    className="flex-1 bg-blue-600 hover:bg-blue-700"
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    View
                  </Button>
                  <Button variant="outline" size="sm">
                    <Download className="h-4 w-4 mr-2" />
                    Download
                  </Button>
                  <Button variant="outline" size="sm">
                    <Share2 className="h-4 w-4" />
                  </Button>
                </>
              ) : (
                <Button
                  size="sm"
                  variant={isRequested ? "outline" : "default"}
                  onClick={() => !isRequested && handleRequestAccess(document.id)}
                  disabled={isRequested}
                  className="flex-1"
                >
                  {isRequested ? (
                    <>
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Access Requested
                    </>
                  ) : (
                    <>
                      <Lock className="h-4 w-4 mr-2" />
                      Request Access
                    </>
                  )}
                </Button>
              )}
            </div>

            {/* Access Notice */}
            {!document.hasAccess && (
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-md p-2">
                <div className="flex items-center gap-2 text-xs text-amber-700 dark:text-amber-300">
                  <AlertCircle className="h-3 w-3" />
                  <span>Restricted to {document.accessLevel}</span>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Policies & Procedures</h1>
          <p className="text-muted-foreground">
            Access company policies, procedures, and important documents
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm">
            <Bell className="h-4 w-4 mr-2" />
            Subscribe to Updates
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowFilters(!showFilters)}>
            <Filter className="h-4 w-4 mr-2" />
            Filters
          </Button>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="bg-blue-100 dark:bg-blue-900/20 p-2 rounded-lg">
                <FileText className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <div className="text-2xl font-bold">{documents.length}</div>
                <div className="text-sm text-muted-foreground">Total Documents</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="bg-emerald-100 dark:bg-emerald-900/20 p-2 rounded-lg">
                <CheckCircle className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div>
                <div className="text-2xl font-bold">{documents.filter(d => d.hasAccess).length}</div>
                <div className="text-sm text-muted-foreground">Accessible</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="bg-yellow-100 dark:bg-yellow-900/20 p-2 rounded-lg">
                <Star className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
              </div>
              <div>
                <div className="text-2xl font-bold">{favoriteDocuments.length}</div>
                <div className="text-sm text-muted-foreground">Favorites</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="bg-purple-100 dark:bg-purple-900/20 p-2 rounded-lg">
                <TrendingUp className="h-5 w-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <div className="text-2xl font-bold">{documents.reduce((sum, doc) => sum + doc.downloadCount, 0)}</div>
                <div className="text-sm text-muted-foreground">Total Downloads</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Controls */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search policies, procedures, and documents..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <div className="flex items-center gap-2">
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="updated">Last Updated</SelectItem>
              <SelectItem value="title">Title</SelectItem>
              <SelectItem value="downloads">Downloads</SelectItem>
              <SelectItem value="category">Category</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
          >
            {sortOrder === 'asc' ? <SortAsc className="h-4 w-4" /> : <SortDesc className="h-4 w-4" />}
          </Button>
          <div className="flex border rounded-md">
            <Button
              variant={viewMode === 'grid' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('grid')}
              className="rounded-r-none"
            >
              <Grid className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('list')}
              className="rounded-l-none"
            >
              <List className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <Card>
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Category</label>
                <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {categories.map(category => (
                      <SelectItem key={category} value={category}>{category}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Department</label>
                <Select value={selectedDepartment} onValueChange={setSelectedDepartment}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Departments</SelectItem>
                    {departments.map(dept => (
                      <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Classification</label>
                <Select value={selectedClassification} onValueChange={setSelectedClassification}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Classifications</SelectItem>
                    {classifications.map(classification => (
                      <SelectItem key={classification} value={classification}>{classification}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Content */}
      <Tabs defaultValue="all" className="space-y-6">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="all">All Documents</TabsTrigger>
          <TabsTrigger value="recent">Recent</TabsTrigger>
          <TabsTrigger value="popular">Popular</TabsTrigger>
          <TabsTrigger value="favorites">Favorites</TabsTrigger>
        </TabsList>

        <TabsContent value="all">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Showing {filteredAndSortedDocuments.length} of {documents.length} documents
              </p>
            </div>
            
            {viewMode === 'grid' ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredAndSortedDocuments.map(document => (
                  <DocumentCard key={document.id} document={document} />
                ))}
              </div>
            ) : (
              <div className="space-y-4">
                {filteredAndSortedDocuments.map(document => (
                  <Card key={document.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-4">
                        <div className="flex-shrink-0">
                          <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
                            <FileText className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-base mb-1">{document.title}</h3>
                          <p className="text-sm text-muted-foreground mb-2 line-clamp-1">
                            {document.description}
                          </p>
                          <div className="flex items-center gap-4 text-xs text-muted-foreground">
                            <span>{document.category}</span>
                            <span>•</span>
                            <span>{new Date(document.lastUpdated).toLocaleDateString()}</span>
                            <span>•</span>
                            <span>{document.downloadCount} downloads</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {document.hasAccess ? (
                            <>
                              <Button size="sm" onClick={() => handleViewDocument(document.title)}>
                                <Eye className="h-4 w-4 mr-2" />
                                View
                              </Button>
                              <Button variant="outline" size="sm">
                                <Download className="h-4 w-4" />
                              </Button>
                            </>
                          ) : (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleRequestAccess(document.id)}
                              disabled={requestedAccess.includes(document.id)}
                            >
                              <Lock className="h-4 w-4 mr-2" />
                              {requestedAccess.includes(document.id) ? 'Requested' : 'Request Access'}
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="recent">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {recentDocuments.map(document => (
              <DocumentCard key={document.id} document={document} />
            ))}
          </div>
        </TabsContent>

        <TabsContent value="popular">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {popularDocuments.map(document => (
              <DocumentCard key={document.id} document={document} />
            ))}
          </div>
        </TabsContent>

        <TabsContent value="favorites">
          {favoritesList.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {favoritesList.map(document => (
                <DocumentCard key={document.id} document={document} />
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="p-8 text-center">
                <Star className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">No favorites yet</h3>
                <p className="text-muted-foreground">
                  Star documents to add them to your favorites for quick access.
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Document Viewer */}
      <DocumentViewer
        isOpen={isDocumentViewerOpen}
        onClose={() => setIsDocumentViewerOpen(false)}
        documentTitle={selectedDocument || ''}
      />
    </div>
  );
}